// IP 合作谈判「上线联动明细」表格（ProTablePlusForm，可复用内包/外包场景）。
// 设计稿结构（树形/分组，merge 纵向合并）：第一层 = 回复 / 意见反馈/说明（父级列，mergeRowSpanKey:['item_key'] 纵向合并整组子行）；
//   第二层 = 序号 / *联动上线日期 / 联动下线日期 / *联动上线时长 / 操作（子行，每行独立）。列序：序号|上线日期|下线日期|上线时长|操作|回复|意见反馈/说明。
// 授权时长已独立为 license_duration 条款（AuthorizationInfoTable），不再在此表；整表一条 solution_detail 条款回复。
// 回复列 + 意见反馈列 + 批量编辑标题复用 NegotiationTable/renderCells 工厂，数据收集走外层 editState（key=item_key）。
//
// 协议化改造（对齐 editable-table 协议：View/FieldView + Capability + ValueAdapter）：
//   能力判定命名化（isInternalEdit 内包数据列可编辑 / isExternalEdit 外包回复列可编辑），值转换经 formatPeriod / formatDurationValue（valueAdapter 语义）；
//   「新增」用 ProTablePlusForm footer 放表格内（对齐期望图底部「＋新增」）；删除禁用 length<=1（>1 行任意行可删）。
//   参考模型：features/mpc-common/src/editable-table/ + components/ProTablePlusForm/$demo/editable-table-showcase。

import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { useMemoizedFn } from 'ahooks';
import {
  ProTablePlusForm,
  type ProTablePlusFormRef,
} from '@mpc/common/src/components/ProTablePlusForm';
import type { AddGroupFormColumn } from '@mpc/common/src/components/ProTablePlusForm/addGroupTable';
import type {
  IpCooperationDemandDetailVoL2Info,
  OnlineLinkItem,
} from '#services/api';
import {
  ActionCell,
  getOpinionTitle,
  OpinionCell,
  createRenderBatchEditTitle,
  type CellContext,
  type NegotiationEditState,
  type NegotiationItem,
} from './renderCells';
import DurationInput, {
  DurationValue,
  formatDurationValue,
} from '../DurationInput';
import RoundDiffCell, {
  DIFF_TEXT_CLASS,
  OLD_VALUE_CLASS,
} from '../RoundDiff/RoundDiffCell';
import type { RoundDiffValue, SolutionDiffMap } from '../RoundDiff/types';
import { Button } from '@bedrock/components';
import { Plus } from '@bedrock/icons-react';

/** 联动周期文本（[begin,end] → 'YYYY-MM ~ YYYY-MM'） */
function formatPeriod(value?: any[]): string {
  return Array.isArray(value) && value.length === 2 && value[0] && value[1]
    ? `${dayjs(value[0]).format('YYYY-MM')} ~ ${dayjs(value[1]).format('YYYY-MM')}`
    : '--';
}

/** 各数据列的只读展示（diff 注入时复用；非 diff 仍走既有 render/editRender） */
const SOLUTION_READONLY_RENDERERS: Record<
  string,
  (value: any, row: any) => React.ReactNode
> = {
  duration_value: (v) => <span>{formatDurationValue(v)}</span>,
  online_period: (v) => <span>{formatPeriod(v)}</span>,
  offline_period: (v) => <span>{formatPeriod(v)}</span>,
};

/** dataIndex → SolutionRowDiff 字段 key */
const SOLUTION_DIFF_FIELD_KEY: Record<
  string,
  keyof NonNullable<SolutionDiffMap[string]>
> = {
  duration_value: 'durationValue',
  online_period: 'onlinePeriod',
  offline_period: 'offlinePeriod',
};

/**
 * 历史轮次对比态：为数据列（duration_value/online_period/offline_period）注入 diff 渲染。
 * 仅当 diffData 传入时启用（外包/非对比场景不传，列结构零变化）；行级 added/deleted 整行着色，
 * 单元格 diff 走 RoundDiffCell（最新值着色 + 历史值删除线）。
 */
function injectSolutionDiffRender(
  cols: AddGroupFormColumn[],
  diffData: SolutionDiffMap
): AddGroupFormColumn[] {
  return cols.map((col) => {
    const fieldKey = SOLUTION_DIFF_FIELD_KEY[col.dataIndex as string];
    if (!fieldKey) {
      if (col.children?.length)
        return {
          ...col,
          children: injectSolutionDiffRender(col.children, diffData),
        };
      return col;
    }
    const originalRender = col.render;
    return {
      ...col,
      render: (value, row, index) => {
        const rd = diffData[String(row?.index_id ?? '')];
        const renderValue = (v: unknown) =>
          originalRender
            ? originalRender(v, row, index)
            : SOLUTION_READONLY_RENDERERS[col.dataIndex as string]?.(v, row);
        if (rd?.rowType === 'added')
          return (
            <span className={DIFF_TEXT_CLASS.added}>{renderValue(value)}</span>
          );
        if (rd?.rowType === 'deleted')
          return <span className={OLD_VALUE_CLASS}>{renderValue(value)}</span>;
        const fieldDiff = rd?.[fieldKey] as RoundDiffValue | undefined;
        if (fieldDiff) {
          return (
            <RoundDiffCell
              diff={fieldDiff}
              renderNewValue={(v) => renderValue(v)}
              renderOldValue={(v) => renderValue(v)}
            />
          );
        }
        return renderValue(value);
      },
    };
  });
}

/** 上线联动明细行（OnlineLinkItem + 表格扩展字段；solution_table 受控值由调用方 buildFormValues 构造） */
export interface SolutionTableRow extends OnlineLinkItem {
  /** 联动上线时长组合值（duration + unit） */
  duration_value?: DurationValue;
  /** 联动上线日期（[begin, end] dayjs 数组，未填为空数组） */
  online_period?: any[];
  /** 联动下线日期（[begin, end] dayjs 数组，未填为空数组） */
  offline_period?: any[];
  /** 谈判条款标识（SOLUTION section item_key；整表一组，回复/意见列据此纵向合并） */
  item_key?: string;
  /** 版权方回复（详情扁平化注入行字段；ActionCell 优先读行字段，itemsByKey 仅兜底） */
  action?: string;
  /** 版权方意见（详情扁平化注入行字段；OpinionCell 优先读行字段，itemsByKey 仅兜底） */
  opinion?: string;
  /** 新增占位行标记（internal 新增行） */
  $addRow?: string[];
}

/** SolutionTable 校验能力（SFormItem watchChildFormValidate 提交时调用） */
export interface SolutionTableRef {
  validateFields: () => Promise<unknown>;
}

export interface SolutionTableProps {
  /** 谈判条款列表（negotiation_items，内部取 SOLUTION section） */
  items?: NegotiationItem[];
  /** 二级单信息（products / auth_info，联动明细行数据源） */
  l2?: IpCooperationDemandDetailVoL2Info;
  /** 编辑状态（key=item_key） */
  editState?: Record<string, NegotiationEditState>;
  /** 是否可编辑 */
  editable?: boolean;
  /** 单条编辑变更回调 */
  onEditChange?: (
    itemKey: string,
    patch: Partial<NegotiationEditState>
  ) => void;
  /** 批量操作（仅作用于本表格条款） */
  onBatchAction?: (action: string, itemKeys?: string[]) => void;
  /** 受控表格数据（solution_table 字段值 = online_links 数组；内包 buildFormValues 初始化，外包不传 → 回退 l2） */
  value?: SolutionTableRow[];
  /** 表格数据变更回调（联动上线时长编辑写回 solution_table 字段） */
  onChange?: (next: SolutionTableRow[]) => void;
  /** 场景模式：internal=内包商务修改（数据列 + 增删行可编辑），
   *  external=外包谈判（仅回复/意见反馈可编辑）；不传时整表只读展示 */
  mode?: 'internal' | 'external';
  /** 历史轮次对比 diff（key=index_id → 各字段新旧值）；传了则数据列改渲染 RoundDiffCell，不传保持原展示 */
  diffData?: SolutionDiffMap;
  /** 对比态开关：仅 compareEnabled=true 时展示 diff 样式（缺省 false）；SecondaryDetailContent 传 compareActive */
  compareEnabled?: boolean;
}

const CurrentDurationInput = ({ value, onChange, editable }: any) =>
  !editable ? (
    <span>{formatDurationValue(value)}</span>
  ) : (
    <DurationInput value={value} onChange={(v) => onChange(v)} />
  );

/** 必填星（对齐 createRenderBatchEditTitle 回复列必填星颜色 #ff4d4f，项目约定红） */
const REQUIRED_STAR = (label: string) => (
  <span className="inline-flex items-center">
    <span className="text-[#ff4d4f]">*</span> {label}
  </span>
);

/** SOLUTION 条款 item_key（整表一条：回复/意见列 mergeRowSpanKey 纵向合并的组键；对齐 utils.ts buildSolutionTableRows 硬编码） */
const SOLUTION_ITEM_KEY = 'solution_detail';

interface SolutionTableInnerProps extends SolutionTableProps {
  /** 内部 ProTablePlusForm ref（增删 + validateFields 委托，供外层 SFormItem watchChildFormValidate 校验） */
  tableRef: React.MutableRefObject<ProTablePlusFormRef | null>;
}

const SolutionTableInner: React.FC<SolutionTableInnerProps> = (props) => {
  const {
    items = [],
    l2,
    editState = {},
    editable = false,
    onEditChange,
    onBatchAction,
    value = [],
    onChange,
    mode,
    diffData,
    compareEnabled,
    tableRef,
  } = props;
  // 对比态开关门控：仅 compareEnabled=true 时展示 diff 样式（缺省 false）；diffData 仅在对比态被读取
  const activeDiffData = compareEnabled ? diffData : undefined;
  // 最新受控 value 引用：render 闭包经 ref 读最新行数（删除禁用/新增判断），避免把 value 加入列依赖
  const valueRef = useRef(value);
  valueRef.current = value;

  // 场景编辑开关（Capability 能力由 mode + editable 共同判定）：
  //  - internal：内包商务修改，数据列（联动上线时长/上线日期/下线日期）可编辑
  //  - external：外包谈判，仅回复/意见反馈可编辑
  const isInternalEdit = mode === 'internal' && editable;
  const isExternalEdit = mode === 'external' && editable;

  // 详情条款按 item_key 索引（回显 action/opinion/offer_value）
  const itemsByKey = useMemo(() => {
    const map: Record<string, NegotiationItem> = {};
    (items || []).forEach((i) => {
      if (i.item_key) map[i.item_key] = i;
    });
    return map;
  }, [items]);

  // 共享单元格渲染上下文（回复 / 意见标题）
  // ⚠️ 不传 onBatchAction：上线联动明细整表仅一条 solution_detail 条款回复（mergeRowSpanKey 纵向合并），无批量编辑意义（用户反馈），
  //    故标题由工厂在 editable 时只渲染「* 回复」，隐藏批量编辑链接（createRenderBatchEditTitle 在 editable && onBatchAction 才显示批量编辑）
  const cellCtx: CellContext = {
    itemsByKey,
    editState,
    editable: isExternalEdit,
    onEditChange,
  };
  const renderBatchEditTitle = createRenderBatchEditTitle(cellCtx);

  // 平铺新增行 / 删除行（internal 商务修改）：外部受控 value 数组增删（merge 版无树形，直接数组操作）
  const addRow = useMemoizedFn(() => {
    onChange?.([
      ...valueRef.current,
      { index_id: uuidv4(), item_key: SOLUTION_ITEM_KEY, $addRow: [] },
    ] as any);
  });
  const deleteRow = useMemoizedFn((row: any) => {
    onChange?.(
      valueRef.current.filter(
        (r) => String(r.index_id) !== String(row.index_id)
      )
    );
  });

  const columns = useMemo<AddGroupFormColumn[]>(() => {
    const cols: AddGroupFormColumn[] = [
      // ① 序号
      {
        dataIndex: 'index_id',
        title: '序号',
        // 外包用 rem 字符串（随视口缩放）；内包保持 px
        width: mode === 'external' ? '60rem' : 60,
        render: (_value, _row, index) => <span>{index + 1 ?? '--'}</span>,
      },
      // ② 联动上线日期（必填 *，month range）
      {
        dataIndex: 'online_period',
        // 必填星仅内包商务修改编辑态显示（对齐回复列头 editable 才带星；只读态不标必填）
        title: isInternalEdit ? REQUIRED_STAR('联动上线日期') : '联动上线日期',
        // 内包只读态加宽到 230（序号60+上线230+下线230+时长120=640，W=1140），使回复/意见列与权益表格对齐；编辑态保持 270。
        width: mode === 'external' ? '200rem' : isInternalEdit ? 270 : 230,
        editRender: ({ row }) => ({
          name: 'date',
          editable: isInternalEdit,
          props: {
            range: true,
            picker: 'month',
            format: 'YYYY-MM',
          },
          // 联动上线日期必填（range 未选/只选一端均不通过）
          rule: [
            {
              validator: (_rule, value, callback) => {
                if (!value?.length || !value[0] || !value[1]) {
                  callback('请选择联动上线日期');
                  return;
                }
                callback();
              },
            },
          ],
        }),
        render: (value) =>
          Array.isArray(value) && value.length === 2 && value[0] && value[1]
            ? `${dayjs(value[0]).format('YYYY-MM')} ~ ${dayjs(value[1]).format('YYYY-MM')}`
            : '--',
      },
      // ③ 联动下线日期（month range）
      {
        dataIndex: 'offline_period',
        title: '联动下线日期',
        width: mode === 'external' ? '200rem' : isInternalEdit ? 270 : 230,
        editRender: ({ row }) => ({
          name: 'date',
          editable: isInternalEdit,
          props: {
            range: true,
            picker: 'month',
            format: 'YYYY-MM',
          },
          rule: [
            // 联动下线日期非必填：仅当填写时才校验「晚于上线日期」，未填写（空数组/单端）视为可空，不设必填校验。
            // 时间校验逻辑对齐 OnlineDetailTable「预计下线窗口」：① 下线最早 > 上线最早；② 下线最晚 > 上线最晚。
            {
              validator: (_rule, value, callback) => {
                if (!value?.length || !value[0] || !value[1]) {
                  callback();
                  return;
                }
                const [offlineBegin, offlineEnd] = value;
                const [onlineBegin, onlineEnd] = row?.online_period || [];
                // 用 YYYY-MM 字符串比较（零填充字典序=时间序），对齐 OnlineDetailTable 的 <= 语义，避免依赖未注册的 dayjs isSameOrBefore 插件
                const month = (v: any) => dayjs(v).format('YYYY-MM');
                if (
                  offlineBegin &&
                  onlineBegin &&
                  month(offlineBegin) <= month(onlineBegin)
                ) {
                  callback('联动下线的最早下线时间必须晚于上线的最早上线时间');
                  return;
                }
                if (
                  offlineEnd &&
                  onlineEnd &&
                  month(offlineEnd) <= month(onlineEnd)
                ) {
                  callback('联动下线的最后下线时间必须晚于上线的最晚上线时间');
                  return;
                }
                callback();
              },
            },
          ],
        }),
        render: (value) =>
          Array.isArray(value) && value.length === 2 && value[0] && value[1]
            ? `${dayjs(value[0]).format('YYYY-MM')} ~ ${dayjs(value[1]).format('YYYY-MM')}`
            : '--',
      },
      // ④ 联动上线时长（必填 *，数值+单位）
      {
        dataIndex: 'duration_value',
        // 必填星仅内包商务修改编辑态显示（对齐回复列头 editable 才带星；只读态不标必填）
        title: isInternalEdit ? REQUIRED_STAR('联动上线时长') : '联动上线时长',
        width: mode === 'external' ? '240rem' : isInternalEdit ? `180px` : 120,
        editRender: ({ row }) => ({
          name: 'custom-component',
          editable: isInternalEdit,
          props: {
            customComp: CurrentDurationInput,
            editable: isInternalEdit,
          },
          // 联动上线时长必填：数值与单位均须填写（镜像 SecondaryOnlineDetailTable 范式；仅内包编辑态配 rule）
          ...(isInternalEdit
            ? {
                rule: [
                  {
                    validator: (_rule, value, callback) => {
                      if (
                        value?.duration == null ||
                        value?.unit == null ||
                        value?.duration === ''
                      ) {
                        callback('请填写联动上线时长');
                        return;
                      }
                      callback();
                    },
                  },
                ],
              }
            : {}),
        }),
      },
      // ⑤ 操作列（仅内包商务修改编辑态：每行仅「删除」；「新增」已移至表格底部/footer）。
      //    列序：置于回复列之前（对齐期望图）。外包只读展示时无增删入口。
      ...(isInternalEdit
        ? [
            {
              dataIndex: 'operation',
              title: '操作',
              width: 60,
              render: (_value, row) => (
                <div className="flex items-center gap-1">
                  <Button
                    danger
                    type="text"
                    // 删除禁用：整表只有 1 行时禁删（防删光）；超过 1 行时任意行（含原本真实记录/新增行）均可删。
                    disabled={valueRef.current.length <= 1}
                    onClick={() => deleteRow(row)}
                  >
                    删除
                  </Button>
                </div>
              ),
            },
          ]
        : []),
      // ⑥ 回复列（* 回复；外包不提供批量编辑——整表单条款无批量意义，标题经 cellCtx 无 onBatchAction 只渲染「* 回复」）—— 树形/分组第一层：mergeRowSpanKey=['item_key'] 纵向合并整组子行（对齐期望图）
      {
        dataIndex: 'action',
        title: renderBatchEditTitle([SOLUTION_ITEM_KEY]),
        mergeRowSpanKey: ['item_key'],
        width: isExternalEdit ? 180 : 100,
        // 外包走查 recZIyp：回复列右侧描边缺失（意见反馈列弹性无宽，verticalBorder 不渲染该侧边框）→ 回复列 th/td 加右边框（仅外包模式，内包保持现状）
        ...(mode === 'external'
          ? {
              onCell: () => ({
                style: { borderRight: '1px solid rgb(223, 224, 229)' },
              }),
              onHeaderCell: () => ({
                style: { borderRight: '1px solid rgb(223, 224, 229)' },
              }),
            }
          : {}),
        editRender: ({ row }) => ({
          name: 'custom-component',
          editable: isExternalEdit,
          props: {
            customComp: ActionCell,
            row,
            itemsByKey,
            editState,
            editable: isExternalEdit,
            onEditChange,
            // 历史轮次对比态：回复 diff（readOnly 时 ActionCell 渲染新旧对比）
            actionDiff: activeDiffData?.[String(row?.index_id ?? '')]?.action,
          },
        }),
      },
      // ⑦ 意见反馈/说明（弹性列吃剩余）—— 树形/分组第一层：mergeRowSpanKey=['item_key'] 纵向合并整组子行（对齐期望图）
      {
        dataIndex: 'opinion',
        title: getOpinionTitle(),
        mergeRowSpanKey: ['item_key'],
        // 外包：不设固定宽（弹性列吃剩余，最小宽 100 防坍缩）；内包保持固定
        ...(mode === 'external' ? { minWidth: 100 } : { width: `400px` }),
        editRender: ({ row }) => ({
          name: 'custom-component',
          editable: isExternalEdit,
          props: {
            customComp: OpinionCell,
            row,
            itemsByKey,
            editState,
            editable: isExternalEdit,
            onEditChange,
            // 历史轮次对比态：意见反馈 diff（readOnly 时 OpinionCell 渲染新旧对比）
            opinionDiff: activeDiffData?.[String(row?.index_id ?? '')]?.opinion,
          },
        }),
      },
    ];
    // 历史轮次对比态：compareEnabled 开启且 diffData 传入时为数据列注入 diff 渲染（无 diffData 时列结构零变化）
    return activeDiffData
      ? injectSolutionDiffRender(cols, activeDiffData)
      : cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    itemsByKey,
    editState,
    editable,
    onEditChange,
    onBatchAction,
    isInternalEdit,
    isExternalEdit,
    mode,
    diffData,
    compareEnabled,
    renderBatchEditTitle,
    addRow,
    deleteRow,
  ]);

  const handleDataChange = useMemoizedFn((newData) => {
    onChange?.(newData);
  });

  return (
    <ProTablePlusForm
      ref={tableRef}
      value={value}
      onChange={handleDataChange}
      columns={columns}
      rowKey={(r) => String(r?.index_id ?? r?.__row_key__ ?? '')}
      verticalBorder={true}
      // 外包谈判场景：表格宽度铺满容器；内包商务修改/只读保持 max-content
      scroll={{ x: mode === 'external' ? '100%' : 'max-content' }}
      // 内包商务修改编辑态：新增行按钮放表格底部（对齐期望图「＋新增」；操作列每行仅「删除」）。
      // ⚠️ pro-table 的 footer 是函数签名 (currentPageData) => ReactNode，传 ReactNode 会报 "footer is not a function"
      footer={
        isInternalEdit
          ? () => (
              // 对齐期望图：footer 无多余 padding/margin，新增按钮用 type="link"
              <div className="flex justify-start">
                <Button
                  size="small"
                  type="link"
                  icon={<Plus />}
                  onClick={() => addRow()}
                >
                  新增
                </Button>
              </div>
            )
          : undefined
      }
    />
  );
};

// SFormItem watchChildFormValidate 会注入 ref 并调用 validateFields 联动校验（委托 ProTablePlusForm 范式）；
// forwardRef 把校验能力转发到内部 ProTablePlusForm，实现提交时三列必填校验
const SolutionTable = forwardRef<SolutionTableRef, SolutionTableProps>(
  (props, ref) => {
    const tableRef = useRef<ProTablePlusFormRef | null>(null);
    useImperativeHandle(
      ref,
      () => ({
        validateFields: () => {
          if (!tableRef.current?.validateFields) return Promise.resolve();
          return tableRef.current.validateFields().catch((error: any) => {
            return Promise.reject(error?.message ?? error);
          });
        },
      }),
      []
    );
    return <SolutionTableInner {...props} tableRef={tableRef} />;
  }
);

export default SolutionTable;
